import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import {
  useBuildingBlockStore,
  type BuildingBlockDto,
  type BuildingBlockType
} from '@/stores/buildingBlock'
import { useProductStore } from '@/stores/product'
import { useMaterialStore } from '@/stores/material'
import { useActivityStore } from '@/stores/activity'
import { useEquipmentFactorStore } from '@/stores/equipmentFactor'
import { useEnvironmentDatabaseStore } from '@/stores/environmentDatabase'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useCalculationEnvironmentDatabaseSelectionStore } from '@/stores/calculationEnvironmentDatabaseSelection'
import { useCalculationForm } from '@/components/calculation-form/composable/useCalculationForm'
import { useCalculationDatabaseSwitch } from '@/components/calculation-form/composable/useCalculationDatabaseSwitch'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import type { BlockSwitchIssueMap } from '@/components/building-block-form/utils/switchValidation'
import { ISSUE_SEVERITIES } from '@/constants/issueSeverity'
import {
  buildCustomDataSignature,
  clearChangedSwitchIssues,
  hasChangedSwitchIssues,
  normalizeDatabaseId,
  pruneResolvedSwitchIssues
} from '@/views/building_blocks/utils/buildingBlockViewUtils'

type ProductEnvironmentDatabase = { environment_database?: number }

export function useBuildingBlockView(props: CalculationFormMixinProps) {
  const { calculationStore, isValid, isReadOnly, currentItem } = useCalculationForm(props)

  const route = useRoute()
  const buildingBlockStore = useBuildingBlockStore()
  const productStore = useProductStore()
  const materialStore = useMaterialStore()
  const activityStore = useActivityStore()
  const equipmentFactorStore = useEquipmentFactorStore()
  const environmentDatabaseStore = useEnvironmentDatabaseStore()
  const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
  const transportConfigStore = useTransportConfigStore()
  const environmentDatabaseSelectionStore = useCalculationEnvironmentDatabaseSelectionStore()
  const { create: addBuildingBlockItem } = buildingBlockStore

  const infoMenu = ref(false)
  const loading = ref(true)

  const projectId = computed(() => Number(route.params.id || 0))
  const calculationId = computed(() =>
    Number(route.query.calculationId || route.params.calculationId || 0)
  )
  const hasCalculationContext = computed(() => calculationId.value > 0)

  const hasCustomBuildingBlocks = computed(
    () =>
      (buildingBlockStore.items || []).length > 0 &&
      buildingBlockStore.items.some(
        (block) => block.calculation === calculationId.value && block.hidden === false
      )
  )

  const customBlockIds = computed(() => {
    const blockIds = new Set<number>()

    for (const block of buildingBlockStore.items || []) {
      if (block.calculation === calculationId.value && block.hidden === false) {
        blockIds.add(block.id)
      }
    }

    return blockIds
  })

  const customMaterialIds = computed(() => {
    const materialIds = new Set<number>()

    for (const material of materialStore.items || []) {
      if (customBlockIds.value.has(material.block)) {
        materialIds.add(material.id)
      }
    }

    return materialIds
  })

  const customDataSignature = computed(() => {
    return buildCustomDataSignature({
      blocks: (buildingBlockStore.items || [])
        .filter((block) => customBlockIds.value.has(block.id))
        .map((block) => ({
          id: block.id,
          name: block.name,
          unit: block.unit
        })),
      materials: (materialStore.items || [])
        .filter((material) => customMaterialIds.value.has(material.id))
        .map((material) => ({
          id: material.id,
          block: material.block,
          product: material.product,
          lifetime: material.lifetime,
          impact_unit: material.impact_unit
        })),
      activities: (activityStore.items || [])
        .filter((activity) => customMaterialIds.value.has(activity.material))
        .map((activity) => ({
          id: activity.id,
          material: activity.material,
          stage: activity.stage,
          mode: activity.mode,
          value: activity.value,
          equipment_factor: activity.equipment_factor
        }))
    })
  })

  const {
    hasValidDatabaseSelection,
    hasSelectedDatabase,
    selectedDatabaseName,
    switchValidationByBlock,
    switchChangedHighlightBaseline,
    operationLoadingVisible,
    operationLoadingMessage,
    isBlockedByMissingIssues,
    clearSwitchHighlights
  } = useCalculationDatabaseSwitch({
    isReadOnly,
    projectId,
    calculationId,
    customDataSignature
  })

  const inferMissingIssuesFromCurrentDatabase = async (
    currentDatabaseId: number
  ): Promise<BlockSwitchIssueMap> => {
    const issues: BlockSwitchIssueMap = {}
    const blocksById: Record<number, BuildingBlockDto> = {}

    for (const block of buildingBlockStore.items || []) {
      blocksById[Number(block.id)] = block
    }

    const customMaterials = (materialStore.items || []).filter((material) =>
      customMaterialIds.value.has(Number(material.id))
    )
    const uniqueProductIds = new Set<number>()

    for (const material of customMaterials) {
      const productId = Number(material.product)
      if (productId > 0) {
        uniqueProductIds.add(productId)
      }
    }

    const productById: Record<number, ProductEnvironmentDatabase> = {}

    await Promise.all(
      Array.from(uniqueProductIds).map(async (productId) => {
        const cached = productStore.items.find((item) => Number(item.id) === productId) as
          | { id: number; environment_database?: number }
          | undefined
        if (cached) {
          productById[productId] = cached
          return
        }
        const fetched = await productStore.fetchItem(productId)
        if (!fetched || typeof fetched === 'boolean') return
        productStore.addItems([fetched])
        productById[productId] = fetched as ProductEnvironmentDatabase
      })
    )

    for (const material of customMaterials) {
      const product = productById[Number(material.product)]
      if (!product) continue
      const isInCurrentDatabase = Number(product?.environment_database) === currentDatabaseId
      if (isInCurrentDatabase) continue

      const blockId = Number(material.block)
      const block = blocksById[blockId]
      if (!block) continue

      if (!issues[blockId]) {
        issues[blockId] = {
          severity: ISSUE_SEVERITIES.MISSING,
          missingRows: [],
          changedRows: []
        }
      }

      issues[blockId].missingRows.push({
        row_index: 0,
        block_id: blockId,
        block_name: block.name,
        material_id: Number(material.id),
        product_name: material.name || 'Unknown material',
        unit: material.unit || ''
      })
    }

    return issues
  }

  const restoreSwitchIssuesAfterReload = async () => {
    if (Object.keys(switchValidationByBlock.value).length) {
      calculationStore.setHasMissingDatabaseSwitchIssues(isBlockedByMissingIssues.value)
      return
    }

    if (!hasCustomBuildingBlocks.value) {
      calculationStore.setHasMissingDatabaseSwitchIssues(false)
      return
    }

    const currentDatabaseId = normalizeDatabaseId(currentItem.value?.database)
    if (!currentDatabaseId) {
      calculationStore.setHasMissingDatabaseSwitchIssues(false)
      return
    }

    const inferredIssues = await inferMissingIssuesFromCurrentDatabase(currentDatabaseId)
    switchValidationByBlock.value = inferredIssues
    switchChangedHighlightBaseline.value = null
    calculationStore.setHasMissingDatabaseSwitchIssues(
      Object.values(inferredIssues).some((issue) => issue.missingRows.length > 0)
    )
  }

  const addBuildingBlock = async (blockType: BuildingBlockType = 'material') => {
    if (isReadOnly.value) return
    if (isBlockedByMissingIssues.value) return
    if (!calculationId.value) return
    const rand = Math.random().toString(16).slice(2, 6)
    const label = blockType === 'equipment' ? 'New equipment block' : 'New building block'
    await addBuildingBlockItem({
      name: `${label} ${rand}`,
      calculation: calculationId.value,
      unit: '',
      materials: [],
      hidden: false,
      selected: false,
      block_type: blockType
    })
  }

  watch(
    isBlockedByMissingIssues,
    (isBlocked) => {
      calculationStore.setHasMissingDatabaseSwitchIssues(isBlocked)
    },
    { immediate: true }
  )

  watch(
    hasValidDatabaseSelection,
    (databaseIsValid) => {
      isValid.value = Boolean(databaseIsValid)
    },
    { immediate: true, flush: 'post' }
  )

  watch(
    [currentItem, calculationId],
    ([calculation, currentCalculationId]) => {
      if (!currentCalculationId || !calculation) return
      environmentDatabaseSelectionStore.setSelectedDatabase(
        currentCalculationId,
        normalizeDatabaseId(calculation.database)
      )
    },
    { immediate: true }
  )

  watch(
    customDataSignature,
    (nextSignature) => {
      if (!Object.keys(switchValidationByBlock.value).length) {
        switchChangedHighlightBaseline.value = null
        calculationStore.setHasMissingDatabaseSwitchIssues(false)
        return
      }

      const prunedIssues = pruneResolvedSwitchIssues(
        switchValidationByBlock.value,
        customBlockIds.value,
        customMaterialIds.value
      )
      switchValidationByBlock.value = prunedIssues
      calculationStore.setHasMissingDatabaseSwitchIssues(
        Object.values(prunedIssues).some((issue) => issue.missingRows.length > 0)
      )

      if (!Object.keys(prunedIssues).length) {
        switchChangedHighlightBaseline.value = null
        return
      }

      if (!hasChangedSwitchIssues(prunedIssues)) {
        switchChangedHighlightBaseline.value = null
        return
      }

      if (!switchChangedHighlightBaseline.value) {
        switchChangedHighlightBaseline.value = nextSignature
        return
      }

      if (nextSignature !== switchChangedHighlightBaseline.value) {
        switchValidationByBlock.value = clearChangedSwitchIssues(prunedIssues)
        switchChangedHighlightBaseline.value = null
        calculationStore.setHasMissingDatabaseSwitchIssues(
          Object.values(switchValidationByBlock.value).some((issue) => issue.missingRows.length > 0)
        )
      }
    },
    { flush: 'post' }
  )

  onMounted(async () => {
    try {
      await Promise.all([
        materialStore.fetchOptions(),
        activityStore.fetchOptions(),
        equipmentFactorStore.fetchItems(),
        ...(projectId.value
          ? [environmentDatabaseStore.fetchItems({ project: projectId.value })]
          : []),
        ...(calculationId.value
          ? [buildingBlockStore.fetchItems({ calculation: calculationId.value })]
          : []),
        ...(calculationId.value
          ? [materialStore.fetchItems({ block__calculation: calculationId.value })]
          : []),
        ...(calculationId.value
          ? [activityStore.fetchItems({ material__block__calculation: calculationId.value })]
          : []),
        ...(calculationId.value
          ? [transportConfigStore.fetchItems({ calculation: calculationId.value })]
          : [])
      ])
    } catch (err) {
      console.error('Error fetching resources:', err)
    } finally {
      const validDatabaseIds: number[] = []

      for (const database of environmentDatabaseStore.items) {
        const databaseId = normalizeDatabaseId(database.id)
        if (databaseId !== null) {
          validDatabaseIds.push(databaseId)
        }
      }

      const selectedCalculationDatabase = normalizeDatabaseId(currentItem.value?.database)
      if (
        currentItem.value &&
        selectedCalculationDatabase &&
        !validDatabaseIds.includes(selectedCalculationDatabase)
      ) {
        const updatedCalculation = {
          ...currentItem.value,
          database: null
        }
        calculationStore.setCurrentItem(updatedCalculation)
        await calculationStore.update(updatedCalculation)
      }

      if (calculationId.value) {
        environmentDatabaseSelectionStore.clearInvalidSelectedDatabase(
          calculationId.value,
          validDatabaseIds
        )
      }

      await restoreSwitchIssuesAfterReload()
      isValid.value = hasValidDatabaseSelection.value
      loading.value = false
    }
  })

  return {
    isReadOnly,
    loading,
    operationLoadingVisible,
    operationLoadingMessage,
    infoMenu,
    hasSelectedDatabase,
    selectedDatabaseName,
    addBuildingBlock,
    calculationId,
    hasCalculationContext,
    hasCustomBuildingBlocks,
    switchValidationByBlock,
    isBlockedByMissingIssues
  }
}
